import { API_BASE } from "../backend/adapter.js";
import { SESSIONS, SIGN_IN } from "../routing/route.js";

/**
 * The owner's session, as the Console holds it (issue 118, PRD M2).
 *
 * The Console holds **nothing**: the backend sets an `HttpOnly` cookie on `/api` that no
 * script can read, and every read the adapter makes carries it because it is same-origin. So
 * "signed in" is not a flag here. It is whatever the next read says, and a 401 from any
 * adapter read (`backend/adapter.ts`; not the player's own media fetches) is the one signal that the session has ended (expired, revoked, never begun), which
 * {@link AuthController.expired} answers by clearing what the screens hold and returning to
 * sign-in.
 *
 * An ID token crosses this module once, on its way to `POST /api/auth/session`, and is not
 * kept. There is no token refresh: the cookie session replaces it (PRD M2).
 */

/** Where an ID token comes from: one Google button press (`google.ts`), or a test's identity. */
export interface IdentityProvider {
  /** What the button says. */
  readonly label: string;
  /** An Identity Platform ID token for the person who just signed in. */
  idToken(): Promise<string>;
}

/** The exchange refused this identity (401): not invited, or not this Console's. */
export class SignInRefused extends Error {
  constructor(readonly status: number) {
    super(`the sign-in was refused (${status})`);
    this.name = "SignInRefused";
  }
}

/** The two calls the session makes. */
export interface OwnerSession {
  exchange(idToken: string): Promise<void>;
  signOut(): Promise<void>;
}

export function createOwnerSession(fetchImpl: typeof fetch = fetch, base = API_BASE): OwnerSession {
  return {
    async exchange(idToken) {
      const response = await fetchImpl(`${base}/auth/session`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id_token: idToken }),
      });
      // Only 401 is a refusal of the identity. The exchange answers 403 for a wrong Origin
      // alone, a deployment fault that must not read as "not invited" (session.test.ts).
      if (response.status === 401) throw new SignInRefused(response.status);
      if (!response.ok) throw new Error(`POST /auth/session answered ${response.status}`);
    },
    async signOut() {
      const response = await fetchImpl(`${base}/auth/signout`, { method: "POST" });
      if (!response.ok) throw new Error(`POST /auth/signout answered ${response.status}`);
    },
  };
}

/** What the controller does to the page. */
export interface AuthEffects {
  /** Drop every owner read the screens hold: rows, media, reading, notices. */
  clear(): void;
  /** Move to a path without a history entry the back button would return to. */
  redirect(path: string): void;
}

export interface AuthController {
  /** Exchange a provider's token and go to the library. Rejects with the exchange's refusal. */
  signIn(provider: IdentityProvider): Promise<void>;
  /** Revoke the session and return to sign-in, clearing the page whatever the server said. */
  signOut(): Promise<void>;
  /** A read answered 401: the session is over. */
  expired(): void;
}

export function createAuthController(session: OwnerSession, effects: AuthEffects): AuthController {
  const leave = () => {
    effects.clear();
    effects.redirect(SIGN_IN);
  };
  return {
    async signIn(provider) {
      const token = await provider.idToken();
      await session.exchange(token);
      effects.redirect(SESSIONS);
    },
    async signOut() {
      // **Cleared even when the server call fails.** A shared machine left showing the library
      // because a network request failed is the thing sign-out exists to prevent (story 14);
      // the cookie then dies at its 168-hour mark (`OWNER_SESSION_LIFETIME`).
      try {
        await session.signOut();
      } finally {
        leave();
      }
    },
    expired: leave,
  };
}
